import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import EmailPreviewEditModal from '../components/EmailPreviewEditModal';
import { NOTICE_OF_AWARD_DOC_ID } from '../utils/grantDocumentIds';
import './GrantSubmission.css';

// Once an application is complete it still has a funding outcome to record. The
// three sub-lists under Completed Applications are these, in this order; an
// application with nothing recorded yet counts as Pending.
const FUNDING_GROUPS = [
  { key: 'pending', label: 'Pending', hint: 'Submitted — waiting on the agency’s decision.' },
  { key: 'funded', label: 'Funded (Active)', hint: 'Awarded and still running. Tracked in Grant & Fund Management.' },
  { key: 'funded_expired', label: 'Funded (Expired)', hint: 'Awarded, and all the work has been completed.' },
  { key: 'not_funded', label: 'Not Funded', hint: 'Declined or withdrawn.' }
];

const FUNDING_LABELS = {
  pending: 'Pending',
  funded: 'Funded (Active)',
  funded_expired: 'Funded (Expired)',
  not_funded: 'Not Funded'
};

// Both funded states carry the same award details; only the label differs.
const FUNDED_STATUSES = ['funded', 'funded_expired'];

// Budget lines on the funded form, matching the columns of the grant record in
// Grant & Fund Management so the two stay in step.
const AWARD_BUDGET_FIELDS = [
  'Personnel Costs',
  'Equipment',
  'Materials & Supplies',
  'Travel',
  'Subawards/Consortium/Contractual Costs',
  'Fee',
  'Overhead/Facilities & Administrative',
  'Other'
];

const EMPTY_AWARD_FORM = {
  'Grant/Contract Number': '',
  'Grant Nickname': '',
  'Project Title': '',
  'Principal Investigator': '',
  'Grant Contact': '',
  'Project Start Date': '',
  'Project End Date': '',
  'Is Active': 'Yes',
  ...Object.fromEntries(AWARD_BUDGET_FIELDS.map(field => [field, '']))
};

const awardTotalBudget = (form) =>
  AWARD_BUDGET_FIELDS.reduce((sum, field) => sum + (parseFloat(form[field]) || 0), 0);

// When an application's questionnaire / budget / documents page is opened, the list's
// scroll position and expanded sections are saved here so coming back (Return to
// Applications or browser back) lands where the user left off. Consumed on restore.
const VIEW_STATE_KEY = 'grantSubmissionViewState';

// The last loaded application lists, kept in memory so returning from an application
// can render the list (and restore the scroll position) immediately instead of
// showing "Loading..." and jumping once the refetch finishes. Cleared on page refresh.
let cachedApplications = null; // { company, applications, active, completed }

const readSavedView = () => {
  try {
    const raw = sessionStorage.getItem(VIEW_STATE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const GrantSubmission = ({ userInfo }) => {
  // Read once on mount; the expansion state below starts from it.
  const savedViewRef = React.useRef(undefined);
  if (savedViewRef.current === undefined) {
    savedViewRef.current = readSavedView();
  }
  // Coming back from an application: start from the cached lists, refresh in the background.
  const startFromCacheRef = React.useRef(undefined);
  if (startFromCacheRef.current === undefined) {
    startFromCacheRef.current = !!(
      savedViewRef.current &&
      cachedApplications &&
      cachedApplications.company === userInfo?.selectedCompany
    );
  }
  const initialLists = startFromCacheRef.current ? cachedApplications : null;
  const [applications, setApplications] = useState(() => initialLists?.applications || []);
  const [activeApplications, setActiveApplications] = useState(() => initialLists?.active || []);
  const [completedApplications, setCompletedApplications] = useState(() => initialLists?.completed || []);
  const [isLoading, setIsLoading] = useState(() => !initialLists);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [applicationToDelete, setApplicationToDelete] = useState(null);
  const [error, setError] = useState(null);

  // New grant portal states
  const [showInitModal, setShowInitModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showContactUsModal, setShowContactUsModal] = useState(false);
  const [showContactsModal, setShowContactsModal] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [selectedGrant, setSelectedGrant] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const fileInputRefs = React.useRef({});

  // Completed applications collapse to a one-line summary; these track what's open.
  const [expandedCompletedIds, setExpandedCompletedIds] = useState(
    () => savedViewRef.current?.expandedCompletedIds || {}
  );
  // Each outcome heading is always visible; the lists underneath start collapsed.
  const [expandedFundingGroups, setExpandedFundingGroups] = useState(() => ({
    pending: false,
    funded: false,
    funded_expired: false,
    not_funded: false,
    ...(savedViewRef.current?.expandedFundingGroups || {})
  }));

  // Award ("this grant was funded") form. Opened when a completed application is
  // moved to Funded, and again whenever the recorded award details are edited.
  const [showAwardForm, setShowAwardForm] = useState(false);
  const [awardFormApp, setAwardFormApp] = useState(null);
  const [awardForm, setAwardForm] = useState(EMPTY_AWARD_FORM);
  // 'create' writes a new grant record; 'edit' updates the existing one, whose
  // number is then fixed because it identifies the record.
  const [awardFormMode, setAwardFormMode] = useState('create');
  // Which funded sub-list the application moves into when the form is saved.
  const [awardTargetStatus, setAwardTargetStatus] = useState('funded');
  const [awardNotices, setAwardNotices] = useState([]);
  const [awardNoticeLabel, setAwardNoticeLabel] = useState('');
  const [uploadingAwardNotice, setUploadingAwardNotice] = useState(false);
  const [savingAward, setSavingAward] = useState(false);
  const [awardFormError, setAwardFormError] = useState('');
  const awardNoticeInputRef = React.useRef(null);

  // Email preview states
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [emailVariables, setEmailVariables] = useState({});
  const [emailRecipient, setEmailRecipient] = useState('');
  const [emailCCList, setEmailCCList] = useState([]);
  const [pendingApplicationId, setPendingApplicationId] = useState(null);
  const [preparingEmail, setPreparingEmail] = useState(false);

  // Approval-request email preview (admin reviews it before the client is emailed)
  const [showApprovalEmailPreview, setShowApprovalEmailPreview] = useState(false);
  const [approvalEmailVariables, setApprovalEmailVariables] = useState({});
  const [approvalEmailRecipients, setApprovalEmailRecipients] = useState([]);
  const [approvalGrantId, setApprovalGrantId] = useState(null);
  const [approvalDeadline, setApprovalDeadline] = useState('');
  // Time of day the review window closes. Defaults to the end of the chosen day.
  const [approvalDeadlineTime, setApprovalDeadlineTime] = useState('23:59');
  const [sendingApprovalRequest, setSendingApprovalRequest] = useState(false);
  // Deadline is chosen at send time — there is no submission-deadline field on the
  // grant itself, so the admin sets it when asking the client to review.
  const [showDeadlineModal, setShowDeadlineModal] = useState(false);
  const [pendingApprovalData, setPendingApprovalData] = useState(null);

  const navigate = useNavigate();

  // Render a deadline in the long form used in the email body. A deadline is
  // either a plain date (the client has until the end of that day) or a date and
  // time, in which case the time is spelled out too.
  const formatDeadline = (value) => {
    if (!value) return 'the submission deadline';
    const hasTime = /T\d{2}:\d{2}/.test(value);
    const parsed = new Date(hasTime ? value : `${value}T00:00:00`);
    if (isNaN(parsed.getTime())) return value;
    const date = parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
    if (!hasTime) return date;
    return `${date} at ${parsed.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
  };

  // A deadline entered as a date plus a time is stored as one value; leaving the
  // time at the end of the day keeps the whole day available, which is what the
  // auto-approval assumes for a bare date.
  const combinedApprovalDeadline = () => {
    if (!approvalDeadline) return '';
    const time = approvalDeadlineTime || '23:59';
    return `${approvalDeadline}T${time}`;
  };

  // The application type answered in the questionnaire (New, Resubmission,
  // Renewal, Continuation, Revision), shown on the collapsed row.
  const applicationTypeOf = (application) =>
    application?.questionnaireData?.answers?.grantApplicationType || '';

  // What a completed application's funding outcome is; nothing recorded = Pending.
  const fundingStatusOf = (application) => {
    const status = application?.fundingStatus;
    return FUNDING_GROUPS.some(g => g.key === status) ? status : 'pending';
  };

  // Helper functions to determine user role
  const isAdmin = () => userInfo?.accountType === 'admin' || userInfo?.accountType === 'superadmin';
  const isClient = () => userInfo?.accountType === 'client' || userInfo?.accountType === 'super_user' || userInfo?.accountType === 'super_client' || userInfo?.accountType === 'founder';

  // Fetch existing grant applications. `silent` refreshes without the loading placeholder.
  const fetchApplications = async ({ silent = false } = {}) => {
    try {
      if (!silent) setIsLoading(true);
      const response = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/grant-applications`);

      if (!response.ok) {
        throw new Error('Failed to fetch grant applications');
      }

      const data = await response.json();
      const allApplications = data.applications || [];

      const applicationsWithCounts = await Promise.all(
        allApplications.map(async (app) => {
          if (app.questionnaireCompleted) {
            const questionnaireData = await fetchQuestionnaireData(app.id);
            const requiredDocsCount = getRequiredDocumentsCount(questionnaireData);

            return {
              ...app,
              totalRequiredDocuments: requiredDocsCount,
              questionnaireData: questionnaireData
            };
          } else {
            // If questionnaire not completed, use base count
            return {
              ...app,
              totalRequiredDocuments: 14,
              questionnaireData: null
            };
          }
        })
      );

      // Separate applications into active and completed using the enhanced applications
      const active = applicationsWithCounts.filter(app =>
        app.status !== 'Completed' && app.status !== 'Approved' && app.status !== 'Rejected'
      ).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)); // Sort by creation date, newest first

      const completed = applicationsWithCounts.filter(app =>
        app.status === 'Completed' || app.status === 'Approved' || app.status === 'Rejected'
      ).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)); // Sort by creation date, newest first

      setApplications(applicationsWithCounts);
      setActiveApplications(active);
      setCompletedApplications(completed);
      cachedApplications = {
        company: userInfo?.selectedCompany,
        applications: applicationsWithCounts,
        active,
        completed
      };
    } catch (error) {
      console.error('Error fetching grant applications:', error);
      setError('Failed to load grant applications. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications({ silent: startFromCacheRef.current });
    loadContacts();
    loadAdmins();
    loadGrantContacts();
  }, []);

  // Return to the saved scroll position once the application list has rendered.
  // A layout effect, so when the list comes from the cache the scroll is restored
  // before the first paint and there's no visible jump.
  useLayoutEffect(() => {
    const saved = savedViewRef.current;
    if (isLoading || !saved) return;
    try {
      sessionStorage.removeItem(VIEW_STATE_KEY);
    } catch {
      // ignore
    }

    const target = saved.scrollY || 0;
    let userScrolled = false;
    const onUserScroll = () => { userScrolled = true; };
    const stopListening = () => {
      window.removeEventListener('wheel', onUserScroll);
      window.removeEventListener('touchmove', onUserScroll);
      window.removeEventListener('keydown', onUserScroll);
    };
    window.addEventListener('wheel', onUserScroll, { passive: true });
    window.addEventListener('touchmove', onUserScroll, { passive: true });
    window.addEventListener('keydown', onUserScroll);

    window.scrollTo(0, target);
    const frame = requestAnimationFrame(() => {
      if (!userScrolled) window.scrollTo(0, target);
    });
    // Contacts load separately and can change the page height, so settle once more.
    const settle = setTimeout(() => {
      if (!userScrolled) window.scrollTo(0, target);
      savedViewRef.current = null;
      stopListening();
    }, 400);

    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(settle);
      stopListening();
    };
  }, [isLoading]);

  // Reload admins when userInfo changes (especially selectedCompany)
  useEffect(() => {
    if (userInfo?.selectedCompany) {
      loadAdmins();
    }
  }, [userInfo?.selectedCompany]);

  const fetchQuestionnaireData = async (applicationId) => {
    try {
      const response = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}/questionnaire`);
      if (response.ok) {
        const data = await response.json();
        return data.questionnaire || null;
      }
    } catch (error) {
      console.error('Error fetching questionnaire data:', error);
    }
    return null;
  };


  const getRequiredDocumentsCount = (questionnaireData) => {
    // Base documents count (reduced from 14 to 12 since we removed the single Budget document)
    let totalDocuments = 12;

    console.log("questionnaire data", questionnaireData.answers.projectSites);

    // Add budget documents for each performing site (2 per site: Budget + Budget Justification)
    if (questionnaireData?.answers?.projectSites && questionnaireData.answers.projectSites.length > 0) {
      totalDocuments += questionnaireData.answers.projectSites.length * 2; // Each site adds 2 documents (budget + justification)
      
      // Add Subrecipient Statement of Intent for additional sites (not primary site)
      if (questionnaireData.answers.projectSites.length > 1) {
        totalDocuments += (questionnaireData.answers.projectSites.length - 1); // One statement per additional site
      }
    } else {
      totalDocuments += 2; // Fallback: base company budget + justification
    }

    // Add conditional documents
    if (questionnaireData?.answers?.grantApplicationType === 'Resubmission') {
      totalDocuments += 1; // Introduction document
    }

    // Human fetal tissue adds the compliance assurance and the sample IRB consent form
    if (questionnaireData?.answers?.humanFetalTissue === 'Yes') {
      totalDocuments += 2;
    }

    // Prior SBIR Phase II awards add the Commercialization History attachment
    if (questionnaireData?.answers?.receivedSBIRPhaseII === 'Yes') {
      totalDocuments += 1;
    }

    // Add Commercialization Plan if needed
    const needsCommercializationPlan = 
      (questionnaireData?.answers?.applicationType === "Phase II") ||
      (questionnaireData?.answers?.applicationType === "Fast-Track") ||
      (questionnaireData?.answers?.applicationType === "Direct Phase II") ||
      (questionnaireData?.answers?.applicationType === "Phase IIA") ||
      (questionnaireData?.answers?.applicationType === "Phase IIB") ||
      (questionnaireData?.answers?.applicationType === "Commercialization Readiness Program") ||
      (questionnaireData?.answers?.applicationType === "Phase I" && questionnaireData?.answers?.agency === "DOE");
    
    if (needsCommercializationPlan) {
      totalDocuments += 1;
    }

    return totalDocuments;
  };

  // Form data for PA/RFA popup
  const [initFormData, setInitFormData] = useState({
    paRfaNumber: '',
    proposalTitle: '',
    submissionDeadline: '',
    contactName: '',
    contactEmail: '',
    additionalContacts: [], // [{ contactName, contactEmail }]
  });

  // Form data for contact management
  const [contactFormData, setContactFormData] = useState({
    contactName: '',
    contactEmail: ''
  });

  // Grant-specific contacts state
  const [grantContacts, setGrantContacts] = useState({});
  const [expandedGrantContacts, setExpandedGrantContacts] = useState(
    () => savedViewRef.current?.expandedGrantContacts || {}
  ); // Track which grants have contacts expanded
  const [showGrantContactModal, setShowGrantContactModal] = useState(false);
  const [selectedGrantForContact, setSelectedGrantForContact] = useState(null);
  const [grantContactFormData, setGrantContactFormData] = useState({
    contactName: '',
    contactEmail: ''
  });

  // Form data for Contact Us
  const [contactUsFormData, setContactUsFormData] = useState({
    subject: '',
    message: '',
    urgency: 'normal',
    selectedAdmin: '',
    selectedContact: ''
  });

  // Form data for approval workflow
  const [approvalFormData, setApprovalFormData] = useState({
    action: '',
    feedback: ''
  });

  // Show PA/RFA popup instead of directly creating application
  const handleCreateApplication = () => {
    setShowInitModal(true);
  };

  // Preview email before initializing grant
  const handlePreviewEmail = async () => {
    const { paRfaNumber, proposalTitle, submissionDeadline, contactName, contactEmail, additionalContacts } = initFormData;

    console.log("initFormData: ", initFormData);

    // Validate all required fields
    if (!proposalTitle.trim()) {
      alert('Proposal title is required');
      return;
    }

    if (!paRfaNumber.trim()) {
      alert('PA or RFA number is required');
      return;
    }

    if (!contactName.trim()) {
      alert('Contact name is required');
      return;
    }

    if (!contactEmail.trim()) {
      alert('Contact email is required');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(contactEmail)) {
      alert('Please enter a valid email address');
      return;
    }

    // Validate additional contacts: each must have both name and valid email if either is filled
    for (let i = 0; i < (additionalContacts || []).length; i += 1) {
      const ac = additionalContacts[i];
      const nameFilled = ac.contactName && ac.contactName.trim();
      const emailFilled = ac.contactEmail && ac.contactEmail.trim();
      if (!nameFilled && !emailFilled) continue; // empty rows are tolerated; will be filtered out
      if (!nameFilled || !emailFilled) {
        alert(`Additional contact #${i + 2} is missing a name or email. Please complete or remove it.`);
        return;
      }
      if (!emailRegex.test(ac.contactEmail.trim())) {
        alert(`Additional contact #${i + 2} has an invalid email address.`);
        return;
      }
    }

    try {
      setPreparingEmail(true);

      // First, create the grant application to get the application ID
      const response = await api.post(`${process.env.REACT_APP_BACKEND_API_URL}/grant-applications`, {
        name: proposalTitle,
        submissionDeadline,
        status: 'In Progress',
        createdAt: new Date().toISOString(),
        paRfaNumber,
        contactName,
        contactEmail
      });

      if (!response.ok) {
        throw new Error('Failed to create grant application');
      }

      const data = await response.json();
      const newApplication = data.application;
      setPendingApplicationId(newApplication.id);

      // Assigned admins are notified with their own dedicated "new grant application
      // created" email (sent server-side on creation), so they are intentionally NOT
      // CC'd on this client-facing document-upload email.
      const adminEmails = [];

      // Fetch company info for UEI and address
      let companyUEI = '';
      let companyAddress = '';
      let companyOfficialName = userInfo?.selectedCompany || '';
      try {
        const companyInfoResponse = await api.get(
          `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/company-info`
        );
        if (companyInfoResponse.ok) {
          const companyInfoData = await companyInfoResponse.json();
          // The address checked as the Grants.gov address on Company Information
          companyAddress = companyInfoData.companyInfo?.grantsGovAddress?.address
            || companyInfoData.companyInfo?.businessAddress || '';
          // Same source the grant questionnaire auto-fills the UEI from: the formation
          // submission, falling back server-side to the SAM Registration admin task.
          companyUEI = companyInfoData.companyInfo?.ueiNumber || '';
          if (companyInfoData.companyInfo?.companyName) {
            companyOfficialName = companyInfoData.companyInfo.companyName;
          }
        }
      } catch (err) {
        console.error('Error fetching company info for email:', err);
      }

      // Fall back to the number attached to an uploaded UEI registration document.
      try {
        if (!companyUEI) {
          const filesResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/files/existing-files`, {
            method: 'GET',
            credentials: 'include',
          });
          if (filesResponse.ok) {
            const files = await filesResponse.json();
            const ueiFile = files.find(f => f.documentType === 'UEI' || (f.documentName && f.documentName.startsWith('UEI')));
            // existing-files returns the literal 'N/A' when no number was recorded.
            if (ueiFile && ueiFile.documentNumber && ueiFile.documentNumber !== 'N/A') {
              companyUEI = ueiFile.documentNumber;
            }
          }
        }
      } catch (err) {
        console.error('Error fetching UEI:', err);
      }

      // Prepare email variables for the template
      const documentsUrl = `${process.env.REACT_APP_FRONTEND_URL || window.location.origin}/grant-submission/documents/${newApplication.id}`;

      const variables = {
        contactName: contactName || '',
        grantTitle: proposalTitle,
        documentsUrl: documentsUrl,
        companyName: companyOfficialName,
        companyUEI: companyUEI || '[UEI TBD]',
        companyAddress: companyAddress || '[Address TBD]'
      };

      setEmailVariables(variables);
      setEmailRecipient(contactEmail);
      setEmailCCList(adminEmails);

      // Clients are the recipients of this email — no value in showing them an editable preview.
      // Send directly. Admins still get the preview modal so they can edit before sending.
      if (isClient()) {
        await sendGrantEmailDirect({
          applicationId: newApplication.id,
          recipient: contactEmail,
          ccList: adminEmails,
          variables,
          additionalContacts: (additionalContacts || []).filter(ac =>
            ac.contactName && ac.contactName.trim() && ac.contactEmail && ac.contactEmail.trim()
          )
        });
        setShowInitModal(false);
      } else {
        // Show the email preview/edit modal for admins
        setShowEmailPreview(true);
        setShowInitModal(false); // Close the init modal
      }
    } catch (error) {
      console.error('Error preparing grant initialization:', error);
      alert('Failed to prepare grant initialization. Please try again.');
    } finally {
      setPreparingEmail(false);
    }
  };

  // Render the grant_initialization template server-side and send directly without
  // showing a preview modal. Used when a client initiates a grant — the client is
  // the recipient and shouldn't see an editable copy of their own email.
  const sendGrantEmailDirect = async ({ applicationId, recipient, ccList, variables, additionalContacts }) => {
    try {
      setIsLoading(true);

      // Render template (subject + html) with variables on the server.
      const previewResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/email-templates/preview`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ templateKey: 'grant_initialization', variables })
      });
      if (!previewResponse.ok) {
        throw new Error('Failed to render grant initialization template');
      }
      const previewData = await previewResponse.json();
      const subject = previewData.subject || '';
      const htmlContent = previewData.htmlContent || '';

      const emailResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/emails/send`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: recipient,
          cc: ccList,
          subject,
          html: htmlContent,
          emailType: 'Grant Initialization'
        })
      });
      if (!emailResponse.ok) {
        throw new Error('Failed to send grant initialization email');
      }

      // Add the primary contact and any additional contacts to the grant.
      const allContacts = [
        { contactName: initFormData.contactName.trim(), contactEmail: initFormData.contactEmail.trim() },
        ...additionalContacts
      ];
      for (const c of allContacts) {
        try {
          await api.post(
            `${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}/contacts`,
            { contactName: c.contactName, contactEmail: c.contactEmail, grantId: applicationId }
          );
        } catch (e) {
          console.error(`Failed to add contact ${c.contactEmail}:`, e);
        }
      }

      setInitFormData({
        paRfaNumber: '',
        proposalTitle: '',
        submissionDeadline: '',
        contactName: '',
        contactEmail: '',
        additionalContacts: []
      });
      setPendingApplicationId(null);
      await fetchApplications();
      await loadGrantContacts();
      alert('Grant initialized successfully and email sent!');
    } catch (error) {
      console.error('Error sending grant initialization email:', error);
      alert('Failed to send grant initialization email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle sending email after user edits/confirms
  const handleSendGrantEmail = async (emailData) => {
    try {
      setIsLoading(true);

      // Send the email with edited content
      const emailResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/emails/send`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          to: emailData.recipientEmail,
          cc: emailData.ccEmails,
          subject: emailData.subject,
          html: emailData.htmlContent,
          emailType: 'Grant Initialization'
        })
      });

      if (!emailResponse.ok) {
        throw new Error('Failed to send email');
      }

      // Email sent successfully, now complete the initialization
      const { contactName, contactEmail, additionalContacts } = initFormData;

      // Step 2: Add the primary contact and any additional contacts as grant-specific contacts
      const contactsToAdd = [
        { contactName: contactName.trim(), contactEmail: contactEmail.trim() },
        ...((additionalContacts || []).filter(ac =>
          ac.contactName && ac.contactName.trim() && ac.contactEmail && ac.contactEmail.trim()
        ).map(ac => ({ contactName: ac.contactName.trim(), contactEmail: ac.contactEmail.trim() })))
      ];

      for (const c of contactsToAdd) {
        try {
          const contactResponse = await api.post(
            `${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${pendingApplicationId}/contacts`,
            { contactName: c.contactName, contactEmail: c.contactEmail, grantId: pendingApplicationId }
          );
          if (!contactResponse.ok) {
            console.error(`Failed to add grant contact ${c.contactEmail}, but application was created`);
          }
        } catch (contactError) {
          console.error(`Error adding grant contact ${c.contactEmail}:`, contactError);
          // Don't fail the entire operation
        }
      }

      // Close modal and refresh applications
      setShowEmailPreview(false);
      setInitFormData({
        paRfaNumber: '',
        proposalTitle: '',
        submissionDeadline: '',
        contactName: '',
        contactEmail: '',
        additionalContacts: []
      });
      setPendingApplicationId(null);

      // Refresh applications list
      await fetchApplications();

      // Refresh grant contacts to show the new contact
      await loadGrantContacts();

      alert('Grant initialized successfully and email sent!');
    } catch (error) {
      console.error('Error sending grant email:', error);
      alert('Failed to send grant initialization email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Grant portal workflow functions
  const loadContacts = async () => {
    try {
      const response = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/contacts`);
      if (response.ok) {
        const contactsData = await response.json();
        setContacts(contactsData);
      }
    } catch (error) {
      console.error('Error loading contacts:', error);
    }
  };

  const loadAdmins = async () => {
    try {
      if (userInfo?.selectedCompany) {
        const response = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/auth/company/${userInfo.selectedCompany}/users`);
        if (response.ok) {
          const data = await response.json();
          // Filter for admin and superadmin users
          const adminUsers = data.users?.filter(user =>
            user.accountType === 'admin' || user.accountType === 'superadmin'
          ) || [];
          setAdmins(adminUsers);
        }
      }
    } catch (error) {
      console.error('Error loading admins:', error);
    }
  };

  const handlePreviewUpload = async (applicationId, file) => {
    try {
      const formData = new FormData();
      formData.append('preview', file);

      const response = await api.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}/preview`,
        formData,
        { 'Content-Type': 'multipart/form-data' }
      );

      if (response.ok) {
        alert('Preview uploaded successfully!');
        // Refresh applications to update status
        await fetchApplications();
      } else {
        const error = await response.json();
        alert(`Failed to upload preview: ${error.error}`);
      }
    } catch (error) {
      console.error('Error uploading preview:', error);
      alert('Failed to upload preview. Please try again.');
    }
  };

  const handlePreviewDownload = async (applicationId, proposalTitle) => {
    try {
      const response = await api.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}/preview`,
        {},
        { responseType: 'blob' }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);

        // Get filename from Content-Disposition header or create a default
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = `${proposalTitle}_preview.pdf`;
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }

        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();

        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        const error = await response.json();
        alert(`Failed to download preview: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error downloading preview:', error);
      alert('Failed to download preview. Please try again.');
    }
  };

  // Step 1: load the recipients + variables so the admin can review the email first.
  const handleSubmitForApproval = async (applicationId) => {
    try {
      const response = await api.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}/preview-approval-email-data`
      );

      if (!response.ok) {
        const error = await response.json();
        alert(`Failed to prepare approval request: ${error.error}`);
        return;
      }

      const data = await response.json();

      if (!data.contacts || data.contacts.length === 0) {
        alert('This grant has no contacts to notify. Add a grant contact before submitting for approval.');
        return;
      }

      setApprovalDeadline(data.defaultDeadline || '');
      setApprovalDeadlineTime('23:59');
      setApprovalGrantId(applicationId);
      setPendingApprovalData(data);
      setShowDeadlineModal(true);
    } catch (error) {
      console.error('Error preparing approval request:', error);
      alert('Failed to prepare approval request. Please try again.');
    }
  };

  // Step 1b: the admin confirms the review deadline, which the email states and which
  // drives auto-approval.
  const handleConfirmDeadline = () => {
    if (!approvalDeadline) {
      alert('Please choose a review deadline.');
      return;
    }

    const data = pendingApprovalData;
    setApprovalEmailRecipients(data.contacts.map(c => c.email));
    setApprovalEmailVariables({
      // Addressed to the group, since every grant contact is asked to review.
      contactName: data.contacts.length === 1 ? (data.contacts[0].name || 'there') : 'all',
      grantTitle: data.grantTitle,
      paRfaNumber: data.paRfaNumber || 'this funding opportunity',
      companyName: data.companyName,
      portalUrl: data.portalUrl,
      approvalDeadline: formatDeadline(combinedApprovalDeadline()),
      adminContact: data.adminContact || ''
    });
    setShowDeadlineModal(false);
    setShowApprovalEmailPreview(true);
  };

  // Step 2: send exactly what the admin reviewed.
  const handleSendApprovalRequest = async (emailData) => {
    try {
      setSendingApprovalRequest(true);

      const response = await api.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${approvalGrantId}/submit-for-approval`,
        {
          subject: emailData.subject,
          htmlContent: emailData.htmlContent,
          recipients: approvalEmailRecipients,
          approvalDeadline: combinedApprovalDeadline()
        }
      );

      const data = await response.json();

      if (response.ok) {
        setShowApprovalEmailPreview(false);
        alert(`Approval request sent to ${(data.sentTo || []).join(', ')}`);
        await fetchApplications();
      } else {
        alert(`Failed to send approval request: ${data.error}`);
      }
    } catch (error) {
      console.error('Error sending approval request:', error);
      alert('Failed to send approval request. Please try again.');
    } finally {
      setSendingApprovalRequest(false);
    }
  };

  const handleApprovalAction = async () => {
    const { action, feedback } = approvalFormData;

    if (action === 'request-changes' && !feedback.trim()) {
      alert('Feedback is required when requesting changes');
      return;
    }

    try {
      const endpoint = action === 'approve' ? 'approve' : 'request-changes';
      const response = await api.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${selectedGrant.id}/${endpoint}`,
        { feedback }
      );

      if (response.ok) {
        setShowApprovalModal(false);
        setSelectedGrant(null);
        setApprovalFormData({ action: '', feedback: '' });
        alert(`Grant ${action === 'approve' ? 'approved' : 'changes requested'} successfully!`);
        window.location.reload();
      } else {
        const error = await response.json();
        alert(`Failed to ${action}: ${error.error}`);
      }
    } catch (error) {
      console.error(`Error with ${action}:`, error);
      alert(`Failed to ${action}. Please try again.`);
    }
  };

  // Grant-specific contact management functions
  const loadGrantContacts = async () => {
    try {
      const response = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/grant-contacts`);
      if (response.ok) {
        const data = await response.json();
        // Group contacts by grant ID
        const contactsByGrant = data.reduce((acc, contact) => {
          if (!acc[contact.grantId]) {
            acc[contact.grantId] = [];
          }
          acc[contact.grantId].push(contact);
          return acc;
        }, {});
        setGrantContacts(contactsByGrant);
      }
    } catch (error) {
      console.error('Error loading grant contacts:', error);
    }
  };

  const handleAddGrantContact = async () => {
    const { contactName, contactEmail } = grantContactFormData;

    if (!contactName.trim() || !contactEmail.trim()) {
      alert('Contact name and email are required');
      return;
    }

    if (!selectedGrantForContact) {
      alert('No grant selected');
      return;
    }

    try {
      const response = await api.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${selectedGrantForContact.id}/contacts`,
        {
          contactName,
          contactEmail,
          grantId: selectedGrantForContact.id
        }
      );

      if (response.ok) {
        setShowGrantContactModal(false);
        setGrantContactFormData({ contactName: '', contactEmail: '' });
        setSelectedGrantForContact(null);
        alert('Grant contact added successfully!');
        loadGrantContacts();
      } else {
        const error = await response.json();
        alert(`Failed to add grant contact: ${error.error}`);
      }
    } catch (error) {
      console.error('Error adding grant contact:', error);
      alert('Failed to add grant contact. Please try again.');
    }
  };

  const handleDeleteGrantContact = async (grantId, contactId) => {
    if (!window.confirm('Are you sure you want to delete this grant contact?')) {
      return;
    }

    try {
      const response = await api.delete(
        `${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${grantId}/contacts/${contactId}`
      );

      if (response.ok) {
        alert('Grant contact deleted successfully!');
        loadGrantContacts();
      } else {
        const error = await response.json();
        alert(`Failed to delete grant contact: ${error.error}`);
      }
    } catch (error) {
      console.error('Error deleting grant contact:', error);
      alert('Failed to delete grant contact. Please try again.');
    }
  };

  const handleEmailAllGrantContacts = (grantId) => {
    const contacts = grantContacts[grantId] || [];
    if (contacts.length === 0) {
      alert('No contacts found for this grant');
      return;
    }

    const emails = contacts.map(contact => contact.contactEmail).join(',');
    const subject = encodeURIComponent('Grant Application Update');
    const body = encodeURIComponent(`Hello,

I am reaching out regarding the grant application: ${applications.find(app => app.id === grantId)?.name || 'Grant Application'}.

Best regards,
StreamLaunch Team`);

    window.open(`mailto:${emails}?subject=${subject}&body=${body}`, '_blank');
  };

  const toggleGrantContacts = (grantId) => {
    setExpandedGrantContacts(prev => ({
      ...prev,
      [grantId]: !prev[grantId]
    }));
  };

  // Contact management functions
  const handleAddContact = async () => {
    const { contactName, contactEmail, role } = contactFormData;

    if (!contactName.trim() || !contactEmail.trim()) {
      alert('Contact name and email are required');
      return;
    }

    try {
      const response = await api.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/contacts`,
        contactFormData
      );

      if (response.ok) {
        setShowContactsModal(false);
        setContactFormData({ contactName: '', contactEmail: '' });
        alert('Contact added successfully!');
        loadContacts();
      } else {
        const error = await response.json();
        alert(`Failed to add contact: ${error.error}`);
      }
    } catch (error) {
      console.error('Error adding contact:', error);
      alert('Failed to add contact. Please try again.');
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) {
      return;
    }

    try {
      const response = await api.delete(
        `${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/contacts/${contactId}`
      );

      if (response.ok) {
        alert('Contact deleted successfully!');
        loadContacts();
      } else {
        const error = await response.json();
        alert(`Failed to delete contact: ${error.error}`);
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
      alert('Failed to delete contact. Please try again.');
    }
  };

  const handleContactUs = async () => {
    const { subject, message, urgency, selectedAdmin, selectedContact } = contactUsFormData;

    if (!subject.trim() || !message.trim()) {
      alert('Subject and message are required');
      return;
    }

    if (isClient() && !selectedAdmin) {
      alert('Please select an administrator to contact');
      return;
    }

    if (isAdmin() && !selectedContact) {
      alert('Please select a grant contact to message');
      return;
    }

    try {
      const response = await api.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/contact`,
        contactUsFormData
      );

      if (response.ok) {
        setShowContactUsModal(false);
        setContactUsFormData({ subject: '', message: '', urgency: 'normal', selectedAdmin: '', selectedContact: '' });
        alert('Contact request submitted successfully!');
      } else {
        const error = await response.json();
        alert(`Failed to submit contact request: ${error.error}`);
      }
    } catch (error) {
      console.error('Error submitting contact request:', error);
      alert('Failed to submit contact request. Please try again.');
    }
  };

  // Saves the current view (see VIEW_STATE_KEY) and opens one of an application's pages.
  const openApplicationPage = (path) => {
    try {
      sessionStorage.setItem(VIEW_STATE_KEY, JSON.stringify({
        scrollY: window.scrollY,
        expandedCompletedIds,
        expandedFundingGroups,
        expandedGrantContacts
      }));
    } catch {
      // Storage unavailable: the page just opens at the top when coming back.
    }
    navigate(path);
  };

  // Navigate to questionnaire page
  const handleQuestionnaire = (applicationId) => {
    openApplicationPage(`/grant-submission/questionnaire/${applicationId}`);
  };

  const handleBudget = (applicationId) => {
    openApplicationPage(`/grant-submission/budget/${applicationId}`);
  };

  // Navigate to documents page
  const handleDocuments = (applicationId) => {
    openApplicationPage(`/grant-submission/documents/${applicationId}`);
  };

  // Just-in-Time documents are only requested once the application itself is done,
  // so the page is reachable from completed applications only.
  const handleJITDocuments = (applicationId) => {
    openApplicationPage(`/grant-submission/jit-documents/${applicationId}`);
  };

  // Notices of Award live in the application's document store, so the funded form
  // and the dedicated page below read the same records.
  const fetchNoticesOfAward = async (applicationId) => {
    try {
      const response = await api.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}`
      );
      if (!response.ok) return [];
      const data = await response.json();
      return (data.application?.documents || [])
        .filter(doc => String(doc.documentId) === String(NOTICE_OF_AWARD_DOC_ID));
    } catch (error) {
      console.error('Error loading Notices of Award:', error);
      return [];
    }
  };

  // Build the funded form. Most of it is already known from the questionnaire, so
  // it is filled in and left editable; anything recorded on a previous save (here
  // or in Grant & Fund Management) wins over the questionnaire.
  const buildAwardForm = async (application) => {
    // The list only attaches questionnaireData for applications whose questionnaire
    // is marked complete, and a grant can reach Completed with a draft one, so fetch
    // it here rather than relying on what the card happens to be carrying.
    const questionnaire = application.questionnaireData
      || await fetchQuestionnaireData(application.id);
    const answers = questionnaire?.answers || {};
    const contacts = grantContacts[application.id] || [];
    const piName = [answers.piFirstName, answers.piMiddleName, answers.piLastName]
      .filter(Boolean).join(' ').trim();

    const prefilled = {
      ...EMPTY_AWARD_FORM,
      'Grant/Contract Number': application.awardGrantNumber || '',
      'Project Title': answers.proposalTitle || application.name || '',
      'Principal Investigator': piName,
      'Grant Contact': contacts[0]?.contactName || application.contactName || '',
      'Project Start Date': application.awardStartDate || answers.startDate || '',
      'Project End Date': application.awardEndDate || answers.endDate || '',
      'Is Active': 'Yes'
    };

    // Already funded: overlay whatever the grant record currently holds so editing
    // here shows the same numbers the financial management system shows.
    if (application.awardGrantNumber) {
      try {
        const response = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/grants`);
        if (response.ok) {
          const grants = await response.json();
          const existing = (Array.isArray(grants) ? grants : [])
            .find(g => g['Grant/Contract Number'] === application.awardGrantNumber);
          if (existing) {
            Object.keys(EMPTY_AWARD_FORM).forEach(field => {
              if (existing[field] !== undefined && existing[field] !== '') {
                prefilled[field] = existing[field];
              }
            });
            return { form: prefilled, mode: 'edit' };
          }
        }
      } catch (error) {
        console.error('Error loading the existing grant record:', error);
      }
    }

    // No matching grant record (never created, or since deleted), so saving writes
    // a new one rather than updating something that isn't there.
    return { form: prefilled, mode: 'create' };
  };

  // targetStatus is which funded sub-list the application lands in once the form
  // is saved; editing an already-funded grant keeps it where it is.
  const openAwardForm = async (application, targetStatus = 'funded') => {
    setAwardFormError('');
    setAwardNoticeLabel('');
    setAwardFormApp(application);
    setAwardTargetStatus(
      FUNDED_STATUSES.includes(targetStatus) ? targetStatus : 'funded'
    );
    const { form, mode } = await buildAwardForm(application);
    setAwardForm(form);
    setAwardFormMode(mode);
    setAwardNotices(await fetchNoticesOfAward(application.id));
    setShowAwardForm(true);
  };

  const closeAwardForm = () => {
    setShowAwardForm(false);
    setAwardFormApp(null);
    setAwardTargetStatus('funded');
    setAwardForm(EMPTY_AWARD_FORM);
    setAwardNotices([]);
    setAwardNoticeLabel('');
    setAwardFormError('');
  };

  // Upload a Notice of Award straight from the funded form. One is required before
  // the grant can be marked funded; more can be added later on its own page.
  const handleAwardNoticeUpload = async (file) => {
    if (!file || !awardFormApp) return;

    setUploadingAwardNotice(true);
    setAwardFormError('');
    try {
      const filename = `NOA_${Date.now()}_${file.name}`;
      const urlResponse = await api.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${awardFormApp.id}/document-upload-url`,
        { documentId: NOTICE_OF_AWARD_DOC_ID, filename, contentType: file.type }
      );
      if (!urlResponse.ok) throw new Error('Failed to get an upload URL');
      const urlData = await urlResponse.json();

      const uploadResponse = await fetch(urlData.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file
      });
      if (!uploadResponse.ok) throw new Error('Failed to upload the file');

      const confirmResponse = await api.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${awardFormApp.id}/confirm-document-upload`,
        {
          documentId: NOTICE_OF_AWARD_DOC_ID,
          filename,
          destination: urlData.destination,
          downloadUrl: urlData.downloadUrl,
          metadata: { label: awardNoticeLabel || '', originalFilename: file.name }
        }
      );
      if (!confirmResponse.ok) throw new Error('Failed to record the upload');
      const confirmData = await confirmResponse.json();

      setAwardNotices(prev => [
        ...prev,
        {
          id: confirmData.document?.id || `${Date.now()}`,
          documentId: String(NOTICE_OF_AWARD_DOC_ID),
          filename,
          uploadDate: new Date().toISOString(),
          fileUrl: urlData.downloadUrl,
          metadata: JSON.stringify({ label: awardNoticeLabel || '' })
        }
      ]);
      setAwardNoticeLabel('');
    } catch (error) {
      console.error('Error uploading Notice of Award:', error);
      setAwardFormError(`Failed to upload the Notice of Award: ${error.message}`);
    } finally {
      setUploadingAwardNotice(false);
      if (awardNoticeInputRef.current) awardNoticeInputRef.current.value = '';
    }
  };

  // Saving the form does two things: write the grant record that Grant Reports and
  // the Grant-Related Documents folders are built from, then mark the application
  // funded so it moves into the Funded sub-list.
  const handleSaveAwardForm = async () => {
    if (!awardFormApp) return;

    const grantNumber = (awardForm['Grant/Contract Number'] || '').trim();
    if (!grantNumber) {
      setAwardFormError('Award Number is required.');
      return;
    }
    if (!(awardForm['Grant Contact'] || '').trim()) {
      setAwardFormError('Grant Contact is required.');
      return;
    }
    if (awardNotices.length === 0) {
      setAwardFormError('A Notice of Award must be uploaded before this grant can be marked as funded.');
      return;
    }

    setSavingAward(true);
    setAwardFormError('');
    try {
      const payload = {
        ...awardForm,
        'Grant/Contract Number': grantNumber,
        'Total Budget': awardTotalBudget(awardForm).toString(),
        ...Object.fromEntries(AWARD_BUDGET_FIELDS.map(field => [field, awardForm[field] || '0']))
      };

      const grantResponse = awardFormMode === 'edit'
        ? await api.put(
            `${process.env.REACT_APP_BACKEND_API_URL}/grants/${encodeURIComponent(grantNumber)}`,
            payload
          )
        : await api.post(`${process.env.REACT_APP_BACKEND_API_URL}/grants`, payload);

      if (!grantResponse.ok) {
        const message = await grantResponse.text().catch(() => '');
        throw new Error(message || 'Failed to save the grant record');
      }

      const statusResponse = await api.put(
        `${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${awardFormApp.id}/funding-status`,
        {
          fundingStatus: awardTargetStatus,
          grantNumber,
          startDate: awardForm['Project Start Date'] || '',
          endDate: awardForm['Project End Date'] || ''
        }
      );

      if (!statusResponse.ok) {
        const data = await statusResponse.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to mark the application as funded');
      }

      const landedIn = awardTargetStatus;
      closeAwardForm();
      await fetchApplications();
      setExpandedFundingGroups(prev => ({ ...prev, [landedIn]: true }));
    } catch (error) {
      console.error('Error saving award details:', error);
      setAwardFormError(error.message || 'Failed to save the award details.');
    } finally {
      setSavingAward(false);
    }
  };

  // The drop-down on a completed application. Moving to Funded needs the award
  // details, so it opens the form instead of saving straight away. Moving between
  // the two funded states reuses the details already recorded.
  const handleFundingStatusChange = async (application, nextStatus) => {
    const currentStatus = fundingStatusOf(application);
    if (nextStatus === currentStatus) return;

    const movingIntoFunded = FUNDED_STATUSES.includes(nextStatus);
    const alreadyFunded = FUNDED_STATUSES.includes(currentStatus);

    if (movingIntoFunded && !alreadyFunded) {
      await openAwardForm(application, nextStatus);
      return;
    }

    if (alreadyFunded && !movingIntoFunded && !window.confirm(
      `Move "${application.name}" out of Funded? The grant record and its documents stay in ` +
      'Grant & Fund Management, but the award details recorded here are cleared.'
    )) {
      return;
    }

    try {
      const response = await api.put(
        `${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${application.id}/funding-status`,
        { fundingStatus: nextStatus }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        alert(`Failed to update the funding status: ${data.error || 'Unknown error'}`);
        return;
      }
      await fetchApplications();
    } catch (error) {
      console.error('Error updating funding status:', error);
      alert('Failed to update the funding status. Please try again.');
    }
  };

  // Undo an approval so a completed application can be corrected and sent round again.
  const handleUnapprove = async (application) => {
    if (!window.confirm(
      `Remove the approval on "${application.name}"? It returns to the ongoing list so the ` +
      'preview can be replaced and sent for approval again.'
    )) {
      return;
    }

    try {
      const response = await api.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${application.id}/unapprove`,
        {}
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        alert(`Failed to remove the approval: ${data.error || 'Unknown error'}`);
        return;
      }
      await fetchApplications();
    } catch (error) {
      console.error('Error unapproving grant:', error);
      alert('Failed to remove the approval. Please try again.');
    }
  };

  // Notice of Award paperwork keeps arriving after the award, so it has its own page.
  const handleNoticeOfAwardDocuments = (applicationId) => {
    openApplicationPage(`/grant-submission/noa-documents/${applicationId}`);
  };

  const isApplicationCompleted = (application) =>
    application.status === 'Completed' ||
    application.status === 'Approved' ||
    application.previewStatus === 'approved';

  const handleShowDeleteConfirmation = (application) => {
    setApplicationToDelete(application);
    setShowDeleteConfirmation(true);
  };

  // Cancel delete
  const handleCancelDelete = () => {
    setApplicationToDelete(null);
    setShowDeleteConfirmation(false);
  };

  // Delete an application
  const handleDeleteApplication = async () => {
    if (!applicationToDelete) return;
    
    try {
      const response = await api.delete(
        `${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationToDelete.id}`
      );

      if (!response.ok) {
        throw new Error('Failed to delete grant application');
      }

      // Remove the deleted application from state
      const updatedApplications = applications.filter(app => app.id !== applicationToDelete.id);
      setApplications(updatedApplications);
      
      // Update active or completed lists based on the application status
      if (activeApplications.some(app => app.id === applicationToDelete.id)) {
        setActiveApplications(activeApplications.filter(app => app.id !== applicationToDelete.id));
      } else {
        setCompletedApplications(completedApplications.filter(app => app.id !== applicationToDelete.id));
      }
      
      setShowDeleteConfirmation(false);
      setApplicationToDelete(null);
    } catch (error) {
      console.error('Error deleting grant application:', error);
      alert('Failed to delete grant application. Please try again.');
    }
  };

  // Render an application card with grant portal features
  const renderApplicationCard = (application) => {
    // Get preview status from application data
    const mockGrantStatus = application.previewStatus || 'draft';

    const getStatusIndicator = (status) => {
      switch (status) {
        case 'draft': return 'Preview: Not uploaded';
        case 'preview_uploaded': return 'Preview: Pending approval';
        case 'pending_approval': return 'Preview: Awaiting approval';
        case 'approved': return 'Preview: Approved';
        case 'changes_required': return 'Preview: Changes required';
        default: return 'Preview: Not uploaded';
      }
    };

    const getStatusClass = (status) => {
      switch (status) {
        case 'approved': return 'approved';
        case 'changes_required': return 'changes';
        default: return '';
      }
    };

    return (
      <div key={application.id} className="application-card">
        <div className="application-info">
          <h3>{application.name}</h3>
          <div className="application-meta">
            <span className={`status-badge ${application.status?.toLowerCase().replace(/\s+/g, '-')}`}>
              {application.status}
            </span>
            <span className="creation-date">
              Created: {new Date(application.createdAt).toLocaleDateString()}
            </span>
            {application.submissionDeadline && (
              <span className="creation-date">
                Deadline: {formatDeadline(application.submissionDeadline)}
              </span>
            )}
          </div>
          <div className="application-progress">
            <div className="progress-label">
              <span>Questionnaire: </span>
              <span className={application.questionnaireCompleted ? 'completed' : 'incomplete'}>
                {application.questionnaireCompleted ? 'Completed' : 'Incomplete'}
              </span>
            </div>
            <div className="progress-label">
              <span>Documents: </span>
              <span className="document-count">
                {application.documentsUploaded || 0}/{application.totalRequiredDocuments || 14} Uploaded
              </span>
            </div>

            {/* Preview status and the file itself belong together, so for admins the
                upload/replace and download controls sit inline with the status
                rather than in the action column. Only the controls that move the
                grant forward (submit for approval, approve on behalf) stay on the
                right. */}
            {isAdmin() ? (
              <div className="preview-status preview-panel">
                <span className={`status-text ${getStatusClass(mockGrantStatus)}`}>
                  {getStatusIndicator(mockGrantStatus)}
                </span>
                <div className="preview-panel-actions">
                  {/* Available until approval so a new file can be sent after the
                      client requests edits. */}
                  {mockGrantStatus !== 'approved' && (
                    <button
                      className="preview-inline-btn"
                      onClick={() => fileInputRefs.current[application.id]?.click()}
                    >
                      {application.previewUrl ? 'Replace Preview' : 'Upload Preview'}
                    </button>
                  )}
                  {application.previewUrl && (
                    <button
                      className="preview-inline-btn"
                      onClick={() => handlePreviewDownload(application.id, application.proposalTitle || application.name)}
                    >
                      Download Preview
                    </button>
                  )}
                  {/* An approval can be taken back if the submission still needs
                      work. It sits with the preview controls because that is what
                      an unapproval sends back for another round. */}
                  {mockGrantStatus === 'approved' && (
                    <button
                      className="unapprove-btn preview-inline-btn"
                      onClick={() => handleUnapprove(application)}
                      title="Return this application to the ongoing list so it can be changed"
                    >
                      Unapprove
                    </button>
                  )}
                </div>
                <input
                  type="file"
                  style={{ display: 'none' }}
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      handlePreviewUpload(application.id, file);
                    }
                    e.target.value = '';
                  }}
                  ref={(input) => {
                    if (input) fileInputRefs.current[application.id] = input;
                  }}
                />
              </div>
            ) : (
              mockGrantStatus !== 'draft' && (
                <div className="preview-status">
                  <span className={`status-text ${getStatusClass(mockGrantStatus)}`}>
                    {getStatusIndicator(mockGrantStatus)}
                  </span>
                </div>
              )
            )}

            {/* Remind the client that silence means approval. */}
            {mockGrantStatus === 'pending_approval' && application.approvalDeadline && isClient() && (
              <div style={{ marginTop: '8px', padding: '10px', backgroundColor: '#fef3c7', border: '1px solid #fde68a', borderRadius: '6px' }}>
                <p style={{ margin: 0, fontSize: '13px', color: '#92400e' }}>
                  Please review by <strong>{formatDeadline(application.approvalDeadline)}</strong>. If we
                  don't hear from you by then, this submission will be auto-approved so it can be
                  submitted on time.
                </p>
              </div>
            )}

            {/* Show client feedback when changes are requested (visible to admins) */}
            {mockGrantStatus === 'changes_required' && application.changesFeedback && isAdmin() && (
              <div style={{ marginTop: '8px', padding: '10px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px' }}>
                <strong style={{ fontSize: '12px', color: '#991b1b' }}>Client Feedback:</strong>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#7f1d1d' }}>{application.changesFeedback}</p>
              </div>
            )}

            {/* Approved: show who signed off, when, and any feedback they left.
                Visible to everyone so the completed card carries the full record. */}
            {mockGrantStatus === 'approved' && (
              <div style={{ marginTop: '8px', padding: '10px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px' }}>
                <strong style={{ fontSize: '12px', color: '#166534' }}>Approval:</strong>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#14532d' }}>
                  {application.approvedBy || 'Approved'}
                  {application.approvedAt && ` on ${new Date(application.approvedAt).toLocaleDateString()}`}
                </p>
                {application.approvalFeedback && (
                  <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#14532d', whiteSpace: 'pre-wrap' }}>
                    <em>"{application.approvalFeedback}"</em>
                  </p>
                )}
              </div>
            )}

            {/* Grant Contacts Section */}
            <div className="grant-contacts-section" style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#374151' }}>Grant Contacts</h4>
                <div>
                  <button
                    onClick={() => {
                      setSelectedGrantForContact(application);
                      setShowGrantContactModal(true);
                    }}
                    style={{
                      fontSize: '12px',
                      padding: '4px 8px',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      marginRight: '8px'
                    }}
                    title="Add contact for this grant"
                  >
                    Add Contacts
                  </button>
                  {isAdmin() && grantContacts[application.id]?.length > 0 && (
                    <button
                      onClick={() => handleEmailAllGrantContacts(application.id)}
                      style={{
                        fontSize: '12px',
                        padding: '4px 8px',
                        backgroundColor: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                      title="Email all contacts for this grant"
                    >
                      Email All
                    </button>
                  )}
                </div>
              </div>

              {grantContacts[application.id]?.length > 0 ? (
                <div style={{ fontSize: '12px' }}>
                  {grantContacts[application.id].map((contact, index) => (
                    <div key={contact.id || index} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 0',
                      borderBottom: index < grantContacts[application.id].length - 1 ? '1px solid #e5e7eb' : 'none'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontWeight: '600', color: '#374151' }}>{contact.contactName}</span>
                        <span style={{ color: '#6b7280' }}>-</span>
                        <span style={{ color: '#6b7280' }}>{contact.contactEmail}</span>
                      </div>
                      {isAdmin() && (
                        <button
                          onClick={() => handleDeleteGrantContact(application.id, contact.id)}
                          style={{
                            fontSize: '10px',
                            padding: '2px 6px',
                            backgroundColor: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '3px',
                            cursor: 'pointer'
                          }}
                          title="Remove this contact"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ margin: 0, fontSize: '12px', color: '#6b7280', fontStyle: 'italic' }}>
                  No contacts added for this grant
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="application-actions">
          {/* Standard buttons */}
          <button
            className="questionnaire-btn"
            onClick={() => handleQuestionnaire(application.id)}
          >
            Questionnaire
          </button>
          <button
            className="documents-btn"
            onClick={() => handleDocuments(application.id)}
          >
            Upload Documents
          </button>

          {/* ADMIN BUTTONS
              Only the controls that move the grant forward live here. Everything
              to do with the preview file itself (upload/replace, download) sits
              next to the preview status on the left, which keeps this column to a
              short stack. */}
          {isAdmin() && (
            <>
              {(mockGrantStatus === 'preview_uploaded' || mockGrantStatus === 'changes_required') && (
                <button
                  className="submit-approval-btn"
                  onClick={() => handleSubmitForApproval(application.id)}
                >
                  {mockGrantStatus === 'changes_required' ? 'Resend for Approval' : 'Submit for Approval'}
                </button>
              )}

              {/* Admins can approve as well, so a grant is never blocked by an
                  unresponsive client. */}
              {mockGrantStatus === 'pending_approval' && (
                <>
                  <button
                    className="submit-approval-btn"
                    onClick={() => handleSubmitForApproval(application.id)}
                  >
                    Resend Approval Request
                  </button>
                  <button
                    className="approve-submission-btn"
                    onClick={() => {
                      setSelectedGrant(application);
                      setApprovalFormData({ action: 'approve', feedback: '' });
                      setShowApprovalModal(true);
                    }}
                  >
                    Approve on Behalf of Client
                  </button>
                </>
              )}

              {/* Show the client what deadline they were given. */}
              {mockGrantStatus === 'pending_approval' && application.approvalDeadline && (
                <span className="approval-deadline-note">
                  Auto-approves after {formatDeadline(application.approvalDeadline)}
                </span>
              )}
            </>
          )}

          {/* CLIENT BUTTONS */}
          {isClient() && (
            <>
              {/* Approve Submission button - only active after admin uploads preview */}
              {mockGrantStatus === 'pending_approval' && (
                <div className="client-approval-actions">
                  <button
                    className="approve-submission-btn"
                    onClick={() => {
                      setSelectedGrant(application);
                      setApprovalFormData({ action: 'approve', feedback: '' });
                      setShowApprovalModal(true);
                    }}
                  >
                    Approve Grant For Submission
                  </button>
                  <button
                    className="request-changes-btn"
                    onClick={() => {
                      setSelectedGrant(application);
                      setApprovalFormData({ action: 'request-changes', feedback: '' });
                      setShowApprovalModal(true);
                    }}
                  >
                    Request Changes
                  </button>
                </div>
              )}

              {/* Download Preview — only once an admin has actually submitted the
                  preview for approval. A merely uploaded preview is still a work in
                  progress and shouldn't be visible to the client yet. */}
              {mockGrantStatus === 'pending_approval' && application.previewUrl && (
                <button
                  className="download-preview-btn"
                  onClick={() => handlePreviewDownload(application.id, application.proposalTitle || application.name)}
                >
                  Download Preview
                </button>
              )}

              {/* Show inactive button when preview not uploaded yet */}
              {(mockGrantStatus === 'draft' || mockGrantStatus === 'preview_uploaded' || mockGrantStatus === 'changes_required') && (
                <button
                  className="approve-submission-btn inactive"
                  disabled
                  title="Button will be active after StreamLaunch Admin uploads preview"
                >
                  Approve Grant For Submission
                </button>
              )}
            </>
          )}

          <button
            className="delete-btn"
            onClick={() => handleShowDeleteConfirmation(application)}
          >
            Delete
          </button>

          {/* Just-in-Time paperwork is only requested after the application is in,
              so it sits apart from the rest of the actions. */}
          {isApplicationCompleted(application) && (
            <button
              className="jit-documents-btn"
              onClick={() => handleJITDocuments(application.id)}
            >
              Just-in-Time Documents
            </button>
          )}

          {/* Notices of Award arrive after the award itself — often one per year —
              so they get their own store, reachable once the grant is funded. */}
          {FUNDED_STATUSES.includes(fundingStatusOf(application)) && (
            <button
              className="noa-documents-btn"
              onClick={() => handleNoticeOfAwardDocuments(application.id)}
            >
              Notice of Award Documents
            </button>
          )}
        </div>
      </div>
    );
  };

  // A completed application collapses to its title plus the funding drop-down, so
  // a long list stays scannable; a funded one also shows the award identifiers,
  // which is usually all anyone needs from the collapsed row.
  const renderCompletedEntry = (application) => {
    const expanded = !!expandedCompletedIds[application.id];
    const status = fundingStatusOf(application);

    return (
      <div key={application.id} className="completed-application-entry">
        <div className="completed-application-header">
          <button
            className="completed-application-toggle"
            aria-expanded={expanded}
            onClick={() => setExpandedCompletedIds(prev => ({
              ...prev,
              [application.id]: !prev[application.id]
            }))}
          >
            <span className="toggle-icon">{expanded ? '▼' : '►'}</span>
            <span className="completed-application-title">{application.name}</span>
            {applicationTypeOf(application) && (
              <span className="completed-application-type">{applicationTypeOf(application)}</span>
            )}
          </button>

          <div className="completed-application-controls">
            {FUNDED_STATUSES.includes(status) && (
              <span className="award-summary">
                {[
                  application.awardGrantNumber ? `#${application.awardGrantNumber}` : null,
                  application.awardStartDate || application.awardEndDate
                    ? `${application.awardStartDate || '?'} → ${application.awardEndDate || '?'}`
                    : null
                ].filter(Boolean).join(' · ')}
              </span>
            )}
            <select
              className={`funding-status-select ${status}`}
              value={status}
              onChange={(e) => handleFundingStatusChange(application, e.target.value)}
              onClick={(e) => e.stopPropagation()}
              title="Move this application between Pending, Funded (Active), Funded (Expired) and Not Funded. Funded (Expired) is chosen here once all the work on the award is finished — nothing new has to be entered."
            >
              {FUNDING_GROUPS.map(group => (
                <option key={group.key} value={group.key}>{FUNDING_LABELS[group.key]}</option>
              ))}
            </select>
          </div>
        </div>

        {expanded && renderApplicationCard(application)}
      </div>
    );
  };

  return (
    <div className="grant-submission-container">
      <h1>Grant Submission Portal</h1>
      
      <div className="grant-submission-header">
        <div className="header-actions">
          <button
            className="create-application-btn"
            onClick={handleCreateApplication}
          >
            Start New Grant Application
          </button>
        </div>
      </div>

      {/* Active Applications Section */}
      <div className="grant-applications-section">
        <h2>Ongoing Grant Applications</h2>
        
        {isLoading ? (
          <div className="loading-indicator">Loading applications...</div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : activeApplications.length === 0 ? (
          <div className="no-applications-message">
            No active grant applications. Click the button above to start a new application.
          </div>
        ) : (
          <div className="applications-list">
            {activeApplications.map(application => renderApplicationCard(application))}
          </div>
        )}
      </div>

      {/* Completed Applications Section — split by funding outcome. The outcome
          headings are always visible; only the lists underneath collapse. */}
      <div className="completed-applications-section">
        <h2>Completed Applications{isLoading ? '' : ` (${completedApplications.length})`}</h2>

        {(
          <div className="completed-applications-content">
            {isLoading ? (
              <div className="loading-indicator">Loading applications...</div>
            ) : completedApplications.length === 0 ? (
              <div className="no-applications-message">
                No completed applications found.
              </div>
            ) : (
              FUNDING_GROUPS.map(group => {
                const inGroup = completedApplications.filter(
                  app => fundingStatusOf(app) === group.key
                );
                const open = !!expandedFundingGroups[group.key];

                return (
                  <div key={group.key} className={`funding-group funding-group-${group.key}`}>
                    <button
                      className="funding-group-toggle"
                      aria-expanded={open}
                      onClick={() => setExpandedFundingGroups(prev => ({
                        ...prev,
                        [group.key]: !prev[group.key]
                      }))}
                    >
                      <span className="toggle-icon">{open ? '▼' : '►'}</span>
                      {group.label} ({inGroup.length})
                      <span className="funding-group-hint">{group.hint}</span>
                    </button>

                    {open && (
                      inGroup.length === 0 ? (
                        <div className="no-applications-message">
                          Nothing in {group.label.toLowerCase()}.
                        </div>
                      ) : (
                        <div className="completed-applications-list">
                          {inGroup.map(application => renderCompletedEntry(application))}
                        </div>
                      )
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="modal-overlay">
          <div className="modal-container">
            <h3>Confirm Deletion</h3>
            <p>
              Are you sure you want to delete "{applicationToDelete?.name}"?
            </p>
            <div className="modal-actions">
              <button
                className="cancel-btn"
                onClick={handleCancelDelete}
              >
                Cancel
              </button>
              <button
                className="confirm-delete-btn"
                onClick={handleDeleteApplication}
              >
                Delete Application
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PA/RFA Initialization Modal */}
      {showInitModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <h3>Start New Grant</h3>
            <p>Please enter the PA or RFA number, proposal title, and primary contact information to begin your grant application.</p>

            {/* Disclaimer */}
            <div className="disclaimer-box" style={{
              backgroundColor: '#fef3c7',
              border: '1px solid #f59e0b',
              borderRadius: '6px',
              padding: '12px',
              marginBottom: '20px'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                <span style={{ color: '#f59e0b', marginRight: '8px', fontSize: '16px' }}>⚠️</span>
                <div>
                  <strong style={{ color: '#92400e' }}>Important Notice:</strong>
                  <p style={{ margin: '4px 0 0 0', color: '#92400e', fontSize: '14px' }}>
                    If the PA/RFA number or proposal title needs to be changed later, a new grant application must be created. Please ensure these details are correct before proceeding.
                  </p>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>To which PA or RFA are you applying? *</label>
              <input
                type="text"
                value={initFormData.paRfaNumber}
                onChange={(e) => setInitFormData({...initFormData, paRfaNumber: e.target.value})}
                placeholder="Enter PA or RFA number"
              />
            </div>
            
            <div className="form-group">
              <label>Proposal Title *</label>
              <textarea
                rows="3"
                value={initFormData.proposalTitle}
                onChange={(e) => setInitFormData({...initFormData, proposalTitle: e.target.value})}
                placeholder="Enter your proposal title (longer titles supported)"
                style={{ resize: 'vertical', minHeight: '80px' }}
              />
            </div>

            <div className="form-group">
              <label>Submission Deadline</label>
              <input
                type="date"
                value={initFormData.submissionDeadline}
                onChange={(e) => setInitFormData({...initFormData, submissionDeadline: e.target.value})}
              />
            </div>
            
            {/* Updated Grant Contact Section */}
            <div style={{
              backgroundColor: '#f3f4f6',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '20px',
              border: '1px solid #d1d5db'
            }}>
              <h4 style={{ marginTop: 0, marginBottom: '12px', color: '#374151', fontSize: '16px' }}>
                Primary Grant Contact
              </h4>
              <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px', marginTop: 0 }}>
                This person will be added as the primary grant contact and will receive the initialization email.
              </p>

              <div className="form-group">
                <label>Contact Name *</label>
                <input
                  type="text"
                  value={initFormData.contactName}
                  onChange={(e) => setInitFormData({...initFormData, contactName: e.target.value})}
                  placeholder="Enter contact's full name"
                />
              </div>

              <div className="form-group">
                <label>Contact Email *</label>
                <input
                  type="email"
                  value={initFormData.contactEmail}
                  onChange={(e) => setInitFormData({...initFormData, contactEmail: e.target.value})}
                  placeholder="Enter contact's email address"
                />
              </div>

              {initFormData.additionalContacts.length > 0 && (
                <div style={{ marginTop: '20px', borderTop: '1px solid #d1d5db', paddingTop: '16px' }}>
                  <h5 style={{ marginTop: 0, marginBottom: '12px', color: '#374151', fontSize: '14px' }}>
                    Additional Grant Contacts
                  </h5>
                  {initFormData.additionalContacts.map((contact, idx) => (
                    <div key={idx} style={{ marginBottom: '12px', padding: '12px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: 600 }}>Contact #{idx + 2}</span>
                        <button
                          type="button"
                          onClick={() => {
                            const next = [...initFormData.additionalContacts];
                            next.splice(idx, 1);
                            setInitFormData({ ...initFormData, additionalContacts: next });
                          }}
                          style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '13px', padding: 0 }}
                        >
                          Remove
                        </button>
                      </div>
                      <div className="form-group" style={{ marginBottom: '8px' }}>
                        <label style={{ fontSize: '13px' }}>Name</label>
                        <input
                          type="text"
                          value={contact.contactName}
                          onChange={(e) => {
                            const next = [...initFormData.additionalContacts];
                            next[idx] = { ...next[idx], contactName: e.target.value };
                            setInitFormData({ ...initFormData, additionalContacts: next });
                          }}
                          placeholder="Enter contact's full name"
                        />
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: '13px' }}>Email</label>
                        <input
                          type="email"
                          value={contact.contactEmail}
                          onChange={(e) => {
                            const next = [...initFormData.additionalContacts];
                            next[idx] = { ...next[idx], contactEmail: e.target.value };
                            setInitFormData({ ...initFormData, additionalContacts: next });
                          }}
                          placeholder="Enter contact's email address"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <button
                type="button"
                onClick={() => setInitFormData({
                  ...initFormData,
                  additionalContacts: [...initFormData.additionalContacts, { contactName: '', contactEmail: '' }]
                })}
                style={{
                  marginTop: '12px',
                  background: 'none',
                  border: '1px dashed #6b7280',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  color: '#374151',
                  cursor: 'pointer',
                  fontSize: '13px',
                  width: '100%'
                }}
              >
                + Add another contact
              </button>

              <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '12px', marginBottom: 0, fontStyle: 'italic' }}>
                Note: Additional contacts can also be added or removed later from the grant's contact list.
              </p>
            </div>

            <div className="modal-actions">
              <button
                className="cancel-btn"
                onClick={() => {
                  setShowInitModal(false);
                  setInitFormData({
                    paRfaNumber: '',
                    proposalTitle: '',
                    submissionDeadline: '',
                    contactName: '',
                    contactEmail: '',
                    additionalContacts: []
                  });
                }}
              >
                Cancel
              </button>
              <button
                className="confirm-btn"
                onClick={handlePreviewEmail}
                disabled={preparingEmail}
              >
                {preparingEmail ? 'Preparing Email...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Grant Contacts Modal */}
      {showContactsModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <h3>Add Grant Contact</h3>
            <p>Add contacts who can approve grant submissions.</p>
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                value={contactFormData.contactName}
                onChange={(e) => setContactFormData({...contactFormData, contactName: e.target.value})}
                placeholder="Enter contact name"
              />
            </div>
            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                value={contactFormData.contactEmail}
                onChange={(e) => setContactFormData({...contactFormData, contactEmail: e.target.value})}
                placeholder="Enter contact email"
              />
            </div>
            <div className="modal-actions">
              <button
                className="cancel-btn"
                onClick={() => setShowContactsModal(false)}
              >
                Cancel
              </button>
              <button
                className="confirm-btn"
                onClick={handleAddContact}
              >
                Add Contact
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approval Modal */}
      {showApprovalModal && selectedGrant && (
        <div className="modal-overlay">
          <div className="modal-container">
            <h3>{approvalFormData.action === 'approve' ? 'Approve Grant' : 'Request Changes'}</h3>
            <p><strong>Grant:</strong> {selectedGrant.name}</p>
            <div className="form-group">
              <label>
                {approvalFormData.action === 'approve' ? 'Feedback (Optional)' : 'Feedback (Required)'}
              </label>
              <textarea
                rows="4"
                value={approvalFormData.feedback}
                onChange={(e) => setApprovalFormData({...approvalFormData, feedback: e.target.value})}
                placeholder={
                  approvalFormData.action === 'approve'
                    ? 'Optional feedback for approval'
                    : 'Please specify what changes are needed'
                }
              />
            </div>
            <div className="modal-actions">
              <button
                className="cancel-btn"
                onClick={() => setShowApprovalModal(false)}
              >
                Cancel
              </button>
              <button
                className="confirm-btn"
                onClick={handleApprovalAction}
              >
                {approvalFormData.action === 'approve' ? 'Approve' : 'Request Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Grant Contact Modal */}
      {showGrantContactModal && selectedGrantForContact && (
        <div className="modal-overlay">
          <div className="modal-container">
            <h3>Add Grant Contact</h3>
            <p>Add a contact for the grant: <strong>{selectedGrantForContact.name}</strong></p>
            <div className="form-group">
              <label>Contact Name *</label>
              <input
                type="text"
                value={grantContactFormData.contactName}
                onChange={(e) => setGrantContactFormData({...grantContactFormData, contactName: e.target.value})}
                placeholder="Enter contact name"
              />
            </div>
            <div className="form-group">
              <label>Contact Email *</label>
              <input
                type="email"
                value={grantContactFormData.contactEmail}
                onChange={(e) => setGrantContactFormData({...grantContactFormData, contactEmail: e.target.value})}
                placeholder="Enter contact email"
              />
            </div>
            <div className="modal-actions">
              <button
                className="cancel-btn"
                onClick={() => {
                  setShowGrantContactModal(false);
                  setSelectedGrantForContact(null);
                  setGrantContactFormData({ contactName: '', contactEmail: '' });
                }}
              >
                Cancel
              </button>
              <button
                className="confirm-btn"
                onClick={handleAddGrantContact}
              >
                Add Contact
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contact Us Modal */}
      {showContactUsModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <h3>Contact Us</h3>
            <p>Send a message to {isClient() ? 'your administrators' : 'grant contacts'}.</p>

            <div className="form-group">
              <label>Subject *</label>
              <input
                type="text"
                value={contactUsFormData.subject}
                onChange={(e) => setContactUsFormData({...contactUsFormData, subject: e.target.value})}
                placeholder="Enter subject"
              />
            </div>

            <div className="form-group">
              <label>Message *</label>
              <textarea
                rows="5"
                value={contactUsFormData.message}
                onChange={(e) => setContactUsFormData({...contactUsFormData, message: e.target.value})}
                placeholder="Enter your message"
              />
            </div>

            <div className="form-group">
              <label>Urgency</label>
              <select
                value={contactUsFormData.urgency}
                onChange={(e) => setContactUsFormData({...contactUsFormData, urgency: e.target.value})}
              >
                <option value="normal">Normal</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            {isClient() && admins.length > 0 && (
              <div className="form-group">
                <label>Select Administrator *</label>
                <select
                  value={contactUsFormData.selectedAdmin}
                  onChange={(e) => setContactUsFormData({...contactUsFormData, selectedAdmin: e.target.value})}
                >
                  <option value="">-- Select Administrator --</option>
                  {admins.map((admin) => (
                    <option key={admin.email} value={admin.email}>
                      {admin.name || admin.email} ({admin.accountType})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {isAdmin() && contacts.length > 0 && (
              <div className="form-group">
                <label>Select Contact *</label>
                <select
                  value={contactUsFormData.selectedContact}
                  onChange={(e) => setContactUsFormData({...contactUsFormData, selectedContact: e.target.value})}
                >
                  <option value="">-- Select Contact --</option>
                  {contacts.map((contact) => (
                    <option key={contact.id} value={contact.contactEmail}>
                      {contact.contactName} ({contact.contactEmail})
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="modal-actions">
              <button
                className="cancel-btn"
                onClick={() => {
                  setShowContactUsModal(false);
                  setContactUsFormData({ subject: '', message: '', urgency: 'normal', selectedAdmin: '', selectedContact: '' });
                }}
              >
                Cancel
              </button>
              <button
                className="confirm-btn"
                onClick={handleContactUs}
              >
                Send Message
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Funded-grant form. Creates (or updates) the grant record that Grant Reports
          and the Grant-Related Documents folders are built from, so the client no
          longer has to re-enter it there. */}
      {showAwardForm && awardFormApp && (
        <div className="modal-overlay">
          <div className="modal-container award-form-modal">
            <h3>{awardFormMode === 'edit' ? 'Edit Award Details' : 'Grant Funded — Award Details'}</h3>
            <p><strong>Application:</strong> {awardFormApp.name}</p>
            <p style={{ fontSize: '14px', color: '#6b7280' }}>
              Most of this is filled in from the grant questionnaire — check it and correct
              anything the award changed. Saving records the grant in Grant &amp; Fund
              Management, where it can also be edited later, and creates its folder under
              Grant-Related Documents.
            </p>

            {awardFormError && (
              <div style={{ padding: '10px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', color: '#991b1b', fontSize: '13px', marginBottom: '16px' }}>
                {awardFormError}
              </div>
            )}

            <div className="form-group">
              <label>Award Number *</label>
              <input
                type="text"
                value={awardForm['Grant/Contract Number']}
                disabled={awardFormMode === 'edit'}
                onChange={(e) => setAwardForm(prev => ({ ...prev, 'Grant/Contract Number': e.target.value }))}
                placeholder="As it appears on the Notice of Award"
              />
              {awardFormMode === 'edit' && (
                <p style={{ fontSize: '12px', color: '#6b7280', margin: '6px 0 0' }}>
                  The number identifies the grant record, so it can&rsquo;t be changed here.
                </p>
              )}
            </div>

            <div className="form-group">
              <label>Grant Nickname</label>
              <input
                type="text"
                maxLength={20}
                value={awardForm['Grant Nickname']}
                onChange={(e) => setAwardForm(prev => ({ ...prev, 'Grant Nickname': e.target.value }))}
                placeholder="Short name (max 20 chars)"
              />
            </div>

            <div className="form-group">
              <label>Project Title</label>
              <textarea
                rows="2"
                value={awardForm['Project Title']}
                onChange={(e) => setAwardForm(prev => ({ ...prev, 'Project Title': e.target.value }))}
              />
            </div>

            <div className="form-group">
              <label>Principal Investigator</label>
              <input
                type="text"
                value={awardForm['Principal Investigator']}
                onChange={(e) => setAwardForm(prev => ({ ...prev, 'Principal Investigator': e.target.value }))}
              />
            </div>

            <div className="form-group">
              <label>Grant Contact *</label>
              <input
                type="text"
                value={awardForm['Grant Contact']}
                onChange={(e) => setAwardForm(prev => ({ ...prev, 'Grant Contact': e.target.value }))}
              />
            </div>

            <div style={{ display: 'flex', gap: '16px' }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label>Project Start Date</label>
                <input
                  type="date"
                  value={awardForm['Project Start Date']}
                  onChange={(e) => setAwardForm(prev => ({ ...prev, 'Project Start Date': e.target.value }))}
                />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>Project End Date</label>
                <input
                  type="date"
                  value={awardForm['Project End Date']}
                  onChange={(e) => setAwardForm(prev => ({ ...prev, 'Project End Date': e.target.value }))}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Status</label>
              <select
                value={awardForm['Is Active']}
                onChange={(e) => setAwardForm(prev => ({ ...prev, 'Is Active': e.target.value }))}
              >
                <option value="Yes">Active</option>
                <option value="No">Inactive</option>
              </select>
            </div>

            <h4 style={{ marginBottom: '8px' }}>Awarded Budget</h4>
            <p style={{ fontSize: '13px', color: '#6b7280', marginTop: 0 }}>
              Enter the awarded amounts. These drive the expenditure reports and can be
              adjusted later from Grant Reports. Personnel Costs is the total for the
              award; once per-person allocations are assigned in Grant Reports, that
              breakdown becomes the figure it uses.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0 16px' }}>
              {AWARD_BUDGET_FIELDS.map(field => (
                <div className="form-group" key={field}>
                  <label>{field}</label>
                  <input
                    type="number"
                    placeholder="0.00"
                    value={awardForm[field]}
                    onChange={(e) => setAwardForm(prev => ({ ...prev, [field]: e.target.value }))}
                  />
                </div>
              ))}
            </div>
            <p style={{ fontWeight: 600, marginTop: 0 }}>
              Total Budget: ${awardTotalBudget(awardForm).toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </p>

            <h4 style={{ marginBottom: '8px' }}>Notice of Award *</h4>
            <p style={{ fontSize: '13px', color: '#6b7280', marginTop: 0 }}>
              At least one Notice of Award is required. Agencies often issue another each
              year — those can be added later from the grant&rsquo;s Notice of Award page.
            </p>

            {awardNotices.length > 0 ? (
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 12px' }}>
                {awardNotices.map(notice => {
                  let label = '';
                  try {
                    label = notice.metadata
                      ? (typeof notice.metadata === 'string' ? JSON.parse(notice.metadata) : notice.metadata).label || ''
                      : '';
                  } catch (parseError) {
                    label = '';
                  }
                  const filename = (notice.filename || '').replace(/^NOA_\d+_/, '');
                  return (
                    <li key={notice.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', padding: '8px 0', borderBottom: '1px solid #e5e7eb', fontSize: '13px' }}>
                      <span>
                        <strong>{label || 'Notice of Award'}</strong>
                        <span style={{ color: '#6b7280' }}> · {filename}</span>
                      </span>
                      {notice.fileUrl && (
                        <span style={{ display: 'flex', gap: '8px' }}>
                          <button
                            type="button"
                            className="preview-inline-btn"
                            onClick={() => window.open(notice.fileUrl, '_blank')}
                          >
                            View
                          </button>
                          <a
                            className="preview-inline-btn"
                            href={notice.fileUrl}
                            download={filename}
                            style={{ textDecoration: 'none' }}
                          >
                            Download
                          </a>
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p style={{ fontSize: '13px', color: '#92400e', marginTop: 0 }}>
                No Notice of Award uploaded yet.
              </p>
            )}

            <div className="form-group">
              <label>Label for the next upload (optional)</label>
              <input
                type="text"
                value={awardNoticeLabel}
                onChange={(e) => setAwardNoticeLabel(e.target.value)}
                placeholder="e.g. Year 1 / 2026"
              />
            </div>
            <input
              type="file"
              ref={awardNoticeInputRef}
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleAwardNoticeUpload(file);
              }}
            />
            <button
              type="button"
              className="preview-inline-btn"
              disabled={uploadingAwardNotice}
              onClick={() => awardNoticeInputRef.current?.click()}
            >
              {uploadingAwardNotice ? 'Uploading...' : '+ Upload Notice of Award'}
            </button>

            <div className="modal-actions">
              <button className="cancel-btn" onClick={closeAwardForm} disabled={savingAward}>
                Cancel
              </button>
              <button className="confirm-btn" onClick={handleSaveAwardForm} disabled={savingAward}>
                {savingAward
                  ? 'Saving...'
                  : awardFormMode === 'edit' ? 'Save Award Details' : 'Mark as Funded'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Preview/Edit Modal */}
      <EmailPreviewEditModal
        isOpen={showEmailPreview}
        onClose={() => {
          setShowEmailPreview(false);
          setShowInitModal(true); // Reopen the init modal if they cancel
        }}
        onSend={handleSendGrantEmail}
        templateKey="grant_initialization"
        variables={emailVariables}
        recipientEmail={emailRecipient}
        ccEmails={emailCCList}
        title="Grant Initialization Email"
        loading={isLoading}
      />

      {/* Review deadline — stated in the email and used for auto-approval. */}
      {showDeadlineModal && pendingApprovalData && (
        <div className="modal-overlay">
          <div className="modal-container">
            <h3>Set Review Deadline</h3>
            <p><strong>Grant:</strong> {pendingApprovalData.grantTitle}</p>
            <p style={{ fontSize: '14px', color: '#6b7280' }}>
              The grant contacts will be asked to approve or request edits by this date. If
              they don't respond, the submission is auto-approved so it can still be filed
              on time.
            </p>
            {pendingApprovalData.submissionDeadline ? (
              <p style={{ fontSize: '13px', color: '#6b7280' }}>
                Pre-filled from this grant's submission deadline
                ({formatDeadline(pendingApprovalData.submissionDeadline)}). Adjust if the
                contacts need to respond sooner.
              </p>
            ) : (
              <p style={{ fontSize: '13px', color: '#92400e' }}>
                This grant has no submission deadline recorded, so the date below is just a
                suggestion.
              </p>
            )}
            <div className="form-group">
              <label>Review deadline *</label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <input
                  type="date"
                  value={approvalDeadline}
                  onChange={(e) => setApprovalDeadline(e.target.value)}
                  style={{ flex: 2 }}
                />
                <input
                  type="time"
                  value={approvalDeadlineTime}
                  onChange={(e) => setApprovalDeadlineTime(e.target.value || '23:59')}
                  style={{ flex: 1 }}
                  title="Time of day the review window closes"
                />
              </div>
              <p style={{ fontSize: '12px', color: '#6b7280', margin: '6px 0 0' }}>
                Defaults to 11:59pm, so the contacts have the whole of the chosen day.
                Set an earlier time if the submission goes out that morning.
              </p>
            </div>
            <p style={{ fontSize: '13px', color: '#6b7280' }}>
              Requests will be sent to: {pendingApprovalData.contacts.map(c => c.email).join(', ')}
            </p>
            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setShowDeadlineModal(false)}>
                Cancel
              </button>
              <button className="confirm-btn" onClick={handleConfirmDeadline}>
                Continue to Email
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approval request email — admins review (and may edit) it before the grant
          contacts are emailed. */}
      <EmailPreviewEditModal
        isOpen={showApprovalEmailPreview}
        onClose={() => setShowApprovalEmailPreview(false)}
        onSend={handleSendApprovalRequest}
        templateKey="grant_preview_approval_request"
        variables={approvalEmailVariables}
        recipientEmail={approvalEmailRecipients.join(', ')}
        title="Grant Preview Approval Request"
        loading={sendingApprovalRequest}
        showEditToggle={isAdmin()}
        allowTemplateUpdate={isAdmin()}
      />

    </div>
  );
};

export default GrantSubmission;